/**
 * Показ готовой выборки (ListResponse): таблица (на телефоне — карточки), доска, календарь-повестка, график.
 * Суммы показываются только если пришли с сервера; поля со скрытыми суммами сюда не попадают вовсе.
 */
import type { ListResponse, SourceField, ViewConfig, ViewRow } from '@epc/contracts';
import { AGG_FNS } from '@epc/contracts';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { type ReactNode, useMemo } from 'react';
import { date, plural } from '../../lib/format';
import { EmptyState } from '../../ui/feedback';
import { formatCell, formatTotal, totalKey } from './util';

/** Ссылка на запись портала: внутренний путь — через маршрутизатор, чтобы не перезагружать страницу */
export function RecordLink({ href, children }: { href: string | null | undefined; children: ReactNode }) {
  if (!href) return <>{children}</>;
  if (href.startsWith('/'))
    return (
      <Link
        to={href as never}
        className="inline-flex min-h-11 items-center text-accent-text underline-offset-4 hover:underline md:min-h-0"
      >
        {children}
      </Link>
    );
  return <>{children}</>;
}

const hrefOf = (row: ViewRow, key: string) => {
  const v = row[`${key}__href`];
  return typeof v === 'string' ? v : null;
};

function Cell({ f, row }: { f: SourceField; row: ViewRow }) {
  const text = formatCell(f, row[f.key]);
  const href = f.linked ? hrefOf(row, f.key) : null;
  return (
    <span className={clsx(f.type === 'number' && 'num')}>
      <RecordLink href={href}>{text}</RecordLink>
    </span>
  );
}

const alignRight = (f: SourceField) => f.type === 'number';

function Totals({ list, config, fields }: { list: ListResponse; config: ViewConfig; fields: SourceField[] }) {
  if (!config.totals.length) return null;
  const byField = new Map(fields.map((f) => [f.key, f]));
  return (
    <dl className="grid gap-x-6 gap-y-1 border-t border-line bg-surface-2/60 px-4 py-3 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
      {config.totals.map((t) => (
        <div key={totalKey(t.fn, t.field)} className="flex items-baseline justify-between gap-3">
          <dt className="text-ink-2">
            {AGG_FNS[t.fn]}: {byField.get(t.field)?.label ?? t.field}
          </dt>
          <dd className="num font-medium">
            {formatTotal(byField.get(t.field), t.fn, list.totals[totalKey(t.fn, t.field)])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Строки как таблица на компьютере и как карточки на телефоне */
function Rows({ columns, rows }: { columns: SourceField[]; rows: ViewRow[] }) {
  const [first, ...rest] = columns;
  if (!first) return null;
  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
              {columns.map((f) => (
                <th
                  key={f.key}
                  scope="col"
                  className={clsx('px-3 py-2 font-medium', alignRight(f) && 'text-right')}
                >
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: строки выборки без собственного ключа
              <tr key={i} className="border-t border-line align-top hover:bg-tile/40">
                {columns.map((f) => (
                  <td
                    key={f.key}
                    className={clsx(
                      'px-3 py-2 [overflow-wrap:anywhere]',
                      f.type === 'date' && 'whitespace-nowrap',
                      alignRight(f) && 'text-right',
                    )}
                  >
                    <Cell f={f} row={r} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="divide-y divide-line md:hidden">
        {rows.map((r, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: строки выборки без собственного ключа
          <li key={i} className="flex flex-col gap-1 px-4 py-3 text-[14px]">
            <span className="font-medium [overflow-wrap:anywhere]">
              <Cell f={first} row={r} />
            </span>
            <dl className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-0.5 text-[13px]">
              {rest.map((f) => (
                <div key={f.key} className="contents">
                  <dt className="text-ink-2">{f.label}</dt>
                  <dd className="[overflow-wrap:anywhere]">
                    <Cell f={f} row={r} />
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

function Board({ list, config }: { list: ListResponse; config: ViewConfig }) {
  const field =
    list.columns.find((f) => f.key === config.boardField && f.type === 'enum') ??
    list.columns.find((f) => f.type === 'enum');
  const title = list.columns[0];
  const lanes = useMemo(() => {
    if (!field) return [];
    const m = new Map<string, ViewRow[]>();
    for (const o of field.options ?? []) m.set(o.value, []);
    for (const r of list.rows) {
      const k = r[field.key] == null ? '' : String(r[field.key]);
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()].filter(([, rows]) => rows.length);
  }, [list.rows, field]);
  if (!field || !title)
    return (
      <EmptyState title="Доску построить не из чего">
        В источнике нет поля-списка (например, статуса).
      </EmptyState>
    );
  return (
    <div className="flex flex-col gap-4 p-4 md:flex-row md:overflow-x-auto">
      {lanes.map(([value, rows]) => (
        <section key={value} className="flex min-w-0 flex-col gap-2 md:w-72 md:shrink-0">
          <h3 className="flex items-baseline justify-between gap-2 text-[13px] font-semibold">
            <span>{field.options?.find((o) => o.value === value)?.label ?? (value || 'Без значения')}</span>
            <span className="num text-ink-2">{rows.length}</span>
          </h3>
          <ul className="flex flex-col gap-2">
            {rows.map((r, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: строки выборки без собственного ключа
              <li key={i} className="rounded-md border border-line bg-surface px-3 py-2 text-[13px]">
                <div className="font-medium [overflow-wrap:anywhere]">
                  <Cell f={title} row={r} />
                </div>
                {list.columns
                  .slice(1, 4)
                  .filter((f) => f.key !== field.key)
                  .map((f) => (
                    <div key={f.key} className="text-ink-2">
                      {f.label}: <Cell f={f} row={r} />
                    </div>
                  ))}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Calendar({ list }: { list: ListResponse }) {
  if (!list.calendar?.length)
    return <EmptyState title="Нет записей с датой">В выбранном периоде записей с датой нет.</EmptyState>;
  return (
    <ul className="divide-y divide-line">
      {list.calendar.map((d) => (
        <li key={d.date} className="grid gap-1 px-4 py-3 sm:grid-cols-[9rem_1fr]">
          <span className="num text-[13px] font-semibold">{date(d.date)}</span>
          <ul className="flex flex-col gap-1 text-[14px]">
            {d.items.map((it) => (
              <li key={it.id} className="[overflow-wrap:anywhere]">
                <RecordLink href={it.href}>{it.title}</RecordLink>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/* ─────────── Графики: столбцы, линия, доли ─────────── */

const numFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
/** Доли различаются подписью и значением в легенде, цвет — только дополнение */
const SHARE_COLORS = [
  'var(--color-ink)',
  'var(--color-accent)',
  'var(--color-success)',
  'var(--color-warning)',
  'var(--color-danger)',
  'var(--color-ink-2)',
];

export function ChartView({ chart }: { chart: NonNullable<ListResponse['chart']> }) {
  const pts = chart.points.filter((p) => p.value !== null) as Array<{ label: string; value: number }>;
  if (!pts.length) return <EmptyState title="Нечего рисовать">По выбранным полям значений нет.</EmptyState>;
  const max = Math.max(...pts.map((p) => p.value), 0);
  const min = Math.min(...pts.map((p) => p.value), 0);
  const span = max - min || 1;
  if (chart.kind === 'pie') {
    const sum = pts.reduce((s, p) => s + Math.max(p.value, 0), 0) || 1;
    return (
      <div className="flex flex-col gap-3 p-4">
        <div
          className="flex h-8 w-full overflow-hidden rounded-md border border-line"
          role="img"
          aria-label={`Доли: ${chart.xLabel}`}
        >
          {pts.map((p, i) => (
            <span
              key={p.label}
              title={`${p.label}: ${numFmt.format(p.value)}`}
              style={{
                width: `${(Math.max(p.value, 0) / sum) * 100}%`,
                background: SHARE_COLORS[i % SHARE_COLORS.length],
              }}
              className="border-r border-surface last:border-r-0"
            />
          ))}
        </div>
        <ul className="grid gap-1 text-[13px] sm:grid-cols-2">
          {pts.map((p, i) => (
            <li key={p.label} className="flex min-h-6 items-center gap-2">
              <span
                aria-hidden
                className="size-3 shrink-0 rounded-sm"
                style={{ background: SHARE_COLORS[i % SHARE_COLORS.length] }}
              />
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{p.label}</span>
              <span className="num shrink-0">
                {numFmt.format(p.value)} ·{' '}
                {numFmt.format(Math.round((Math.max(p.value, 0) / sum) * 1000) / 10)}%
              </span>
            </li>
          ))}
        </ul>
        <p className="text-[12px] text-ink-2">{chart.yLabel}</p>
      </div>
    );
  }
  if (chart.kind === 'line' && pts.length > 1) {
    const W = 600;
    const H = 180;
    const x = (i: number) => 40 + (i / (pts.length - 1)) * (W - 56);
    const y = (v: number) => 12 + (1 - (v - min) / span) * (H - 36);
    const d = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.value)}`).join(' ');
    return (
      <div className="p-4">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full"
          role="img"
          aria-label={`${chart.yLabel} по полю «${chart.xLabel}»`}
        >
          <line x1={40} x2={W - 16} y1={y(min)} y2={y(min)} stroke="var(--color-line-strong)" />
          <line x1={40} x2={40} y1={12} y2={H - 24} stroke="var(--color-line-strong)" />
          <text x={36} y={y(max) + 4} textAnchor="end" fontSize={11} fill="var(--color-ink-2)">
            {numFmt.format(max)}
          </text>
          <text x={36} y={y(min) + 4} textAnchor="end" fontSize={11} fill="var(--color-ink-2)">
            {numFmt.format(min)}
          </text>
          <path d={d} fill="none" stroke="var(--color-ink)" strokeWidth={2} />
          {pts.map((p, i) => (
            <circle key={p.label} cx={x(i)} cy={y(p.value)} r={3.5} fill="var(--color-ink)">
              <title>{`${p.label}: ${numFmt.format(p.value)}`}</title>
            </circle>
          ))}
          <text x={40} y={H - 8} fontSize={11} fill="var(--color-ink-2)">
            {pts[0]?.label}
          </text>
          <text x={W - 16} y={H - 8} textAnchor="end" fontSize={11} fill="var(--color-ink-2)">
            {pts.at(-1)?.label}
          </text>
        </svg>
        <p className="mt-1 text-[12px] text-ink-2">
          {chart.yLabel} по полю «{chart.xLabel}»
        </p>
      </div>
    );
  }
  // Столбцы: подписи слева, значение справа от столбца — читается на телефоне без осей
  return (
    <div className="p-4">
      <ul className="flex flex-col gap-1.5" aria-label={`${chart.yLabel} по полю «${chart.xLabel}»`}>
        {pts.map((p) => (
          <li
            key={p.label}
            className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-center gap-x-3 text-[13px] sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
          >
            <span className="[overflow-wrap:anywhere]">{p.label}</span>
            <span className="flex items-center gap-2">
              <span
                className="h-4 rounded-sm bg-ink"
                style={{ width: `${Math.max(2, ((Math.max(p.value, 0) - Math.min(min, 0)) / span) * 100)}%` }}
              />
              <span className="num shrink-0">{numFmt.format(p.value)}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[12px] text-ink-2">
        {chart.yLabel} по полю «{chart.xLabel}»
      </p>
    </div>
  );
}

/** Выборка целиком: вид по настройке, группы с итогами, общие итоги */
export function ResultView({
  list,
  config,
  rows,
  fields,
}: {
  list: ListResponse;
  config: ViewConfig;
  rows?: ViewRow[];
  /** Все поля источника: итоги и подписи групп могут быть по полю, которого нет среди колонок */
  fields?: SourceField[];
}) {
  const all = fields ?? list.columns;
  const shown = rows ?? list.rows;
  const kind = config.viewKind;
  if (kind === 'chart' && list.chart)
    return (
      <>
        <ChartView chart={list.chart} />
        <Totals list={list} config={config} fields={all} />
      </>
    );
  if (kind === 'calendar') return <Calendar list={list} />;
  if (kind === 'board') return <Board list={{ ...list, rows: shown }} config={config} />;
  if (!shown.length && !list.groups?.length)
    return (
      <EmptyState title="Записей нет">
        Нет записей, подходящих под настройку, или у вас нет права на данные этого списка.
      </EmptyState>
    );
  if (list.groups?.length)
    return (
      <>
        {list.groups.map((g) => (
          <section key={g.keys.join('\u0000')} className="border-b border-line last:border-b-0">
            <h3 className="flex flex-wrap items-baseline justify-between gap-2 bg-tile/60 px-4 py-2 text-[13px] font-semibold">
              <span className="[overflow-wrap:anywhere]">{g.keys.join(' › ') || 'Без значения'}</span>
              <span className="num font-normal text-ink-2">
                {plural(g.count, 'запись', 'записи', 'записей')}
                {config.totals
                  .filter((t) => t.fn !== 'count')
                  .map((t) => {
                    const f = all.find((c) => c.key === t.field);
                    return ` · ${AGG_FNS[t.fn].toLowerCase()} «${f?.label ?? t.field}»: ${formatTotal(f, t.fn, g.totals[totalKey(t.fn, t.field)])}`;
                  })
                  .join('')}
              </span>
            </h3>
            <Rows columns={list.columns} rows={g.rows} />
            {g.truncated ? (
              <p className="px-4 py-2 text-[12px] text-ink-2">
                Показаны не все записи группы: уточните отбор.
              </p>
            ) : null}
          </section>
        ))}
        <Totals list={list} config={config} fields={all} />
      </>
    );
  return (
    <>
      <Rows columns={list.columns} rows={shown} />
      <Totals list={list} config={config} fields={all} />
    </>
  );
}
